import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Pin, ChevronDown, ChevronUp, Paperclip, Download, X, Trash2, Pencil, Send, Loader2, Check, AtSign,
} from 'lucide-react';
import { Button } from './ui/button';
import { useUserNames } from '../context/UserNamesContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtWhen = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return iso; }
};

/**
 * Team Board — a shared, chat-style note pinned to the top of Communications.
 * Shows a NEW badge when edited since the user last viewed it; each user can
 * minimize/expand on their own. Supports multiple attachments per post.
 */
export default function TeamBoard() {
  const { resolveName } = useUserNames();
  const [board, setBoard] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editMentions, setEditMentions] = useState([]); // selected user ids while editing
  const [mentionTarget, setMentionTarget] = useState('compose'); // 'compose' | 'edit'
  const [taggable, setTaggable] = useState([]);
  const [mentions, setMentions] = useState([]); // selected user ids
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null); // active "@..." query or null
  const [mentionTriggerStart, setMentionTriggerStart] = useState(0); // index of the '@'
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
  const fileRef = useRef(null);
  const textareaRef = useRef(null);
  const editTextareaRef = useRef(null);
  const seenRef = useRef(false);

  // Auto-grow both composers so the whole message is visible; grows while
  // typing and (for edit) opens sized to the full existing note.
  const autosize = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    const h = Math.min(el.scrollHeight, 480);
    el.style.height = `${h}px`;
    el.style.overflowY = el.scrollHeight > 480 ? 'auto' : 'hidden';
  };
  useLayoutEffect(() => { autosize(textareaRef.current); }, [text]);
  useLayoutEffect(() => { if (editingId) autosize(editTextareaRef.current); }, [editText, editingId]);

  const fetchBoard = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/comms/board`);
      setBoard(r.data);
      // Auto-expand when there's something new; otherwise honor the user's pref.
      setCollapsed(r.data.is_new ? false : !!r.data.minimized);
    } catch (e) {
      // Non-fatal — board just won't show
      console.error('Team board load failed', e);
    }
  }, []);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);
  useEffect(() => {
    axios.get(`${API}/comms/board/taggable-users`).then((r) => setTaggable(r.data || [])).catch(() => {});
  }, []);

  // When the board is open AND has unseen edits, clear NEW for this user once.
  useEffect(() => {
    if (board?.is_new && !collapsed && !seenRef.current) {
      seenRef.current = true;
      axios.post(`${API}/comms/board/seen`).then(() => {
        setBoard((b) => (b ? { ...b, is_new: false } : b));
      }).catch(() => {});
    }
  }, [board, collapsed]);

  const toggle = async () => {
    const next = !collapsed;
    setCollapsed(next);
    try { await axios.post(`${API}/comms/board/minimize`, { minimized: next }); } catch {}
    if (!next) seenRef.current = false; // allow re-marking seen on expand
  };

  const post = async () => {
    if (!text.trim() && files.length === 0) return;
    setPosting(true);
    try {
      const fd = new FormData();
      fd.append('content', text);
      if (mentions.length) fd.append('mentions', JSON.stringify(mentions));
      files.forEach((f) => fd.append('files', f));
      await axios.post(`${API}/comms/board/posts`, fd);
      setText('');
      setFiles([]);
      setMentions([]);
      setShowTagPicker(false);
      setMentionQuery(null);
      if (fileRef.current) fileRef.current.value = '';
      await fetchBoard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not post');
    } finally {
      setPosting(false);
    }
  };

  const toggleMention = (id) => {
    setMentions((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));
  };

  // --- Inline @mention typeahead -------------------------------------------
  // Detect an active "@query" token immediately before the caret.
  const detectMention = (value, caret) => {
    const upToCaret = value.slice(0, caret);
    const match = upToCaret.match(/(?:^|\s)@([\w.'-]*)$/);
    if (match) {
      setMentionQuery(match[1]);
      setMentionTriggerStart(caret - match[1].length - 1); // index of '@'
      setMentionActiveIndex(0);
    } else {
      setMentionQuery(null);
    }
  };

  const onTextChange = (e) => {
    const value = e.target.value;
    setText(value);
    setMentionTarget('compose');
    const caret = e.target.selectionStart ?? value.length;
    detectMention(value, caret);
  };

  const onEditTextChange = (e) => {
    const value = e.target.value;
    setEditText(value);
    setMentionTarget('edit');
    const caret = e.target.selectionStart ?? value.length;
    detectMention(value, caret);
  };

  const mentionMatches = mentionQuery !== null
    ? taggable
        .filter((u) => (u.name || '').toLowerCase().includes(mentionQuery.toLowerCase()))
        .slice(0, 6)
    : [];

  const selectMention = (u) => {
    if (mentionTarget === 'edit') {
      const ta = editTextareaRef.current;
      const caret = ta?.selectionStart ?? editText.length;
      const before = editText.slice(0, mentionTriggerStart);
      const after = editText.slice(caret);
      const insert = `@${u.name} `;
      const newText = before + insert + after;
      setEditText(newText);
      setEditMentions((m) => (m.includes(u.id) ? m : [...m, u.id]));
      setMentionQuery(null);
      const newCaret = (before + insert).length;
      requestAnimationFrame(() => { if (ta) { ta.focus(); ta.setSelectionRange(newCaret, newCaret); } });
      return;
    }
    const caret = textareaRef.current?.selectionStart ?? text.length;
    const before = text.slice(0, mentionTriggerStart);
    const after = text.slice(caret);
    const insert = `@${u.name} `;
    const newText = before + insert + after;
    setText(newText);
    setMentions((m) => (m.includes(u.id) ? m : [...m, u.id]));
    setMentionQuery(null);
    const newCaret = (before + insert).length;
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (ta) { ta.focus(); ta.setSelectionRange(newCaret, newCaret); }
    });
  };

  const onComposerKeyDown = (e) => {
    if (mentionQuery === null || mentionMatches.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionActiveIndex((i) => (i + 1) % mentionMatches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionActiveIndex((i) => (i - 1 + mentionMatches.length) % mentionMatches.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      selectMention(mentionMatches[Math.min(mentionActiveIndex, mentionMatches.length - 1)]);
    } else if (e.key === 'Escape') {
      setMentionQuery(null);
    }
  };

  const beginEdit = (p) => {
    setEditingId(p.id);
    setEditText(p.content);
    setEditMentions((p.mentions || []).map((m) => m.user_id));
    setMentionQuery(null);
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(`${API}/comms/board/posts/${id}`, { content: editText, mentions: editMentions });
      setEditingId(null);
      setMentionQuery(null);
      await fetchBoard();
    } catch (e) { toast.error('Could not save'); }
  };

  const activeMentions = mentionTarget === 'edit' ? editMentions : mentions;

  const remove = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try { await axios.delete(`${API}/comms/board/posts/${id}`); await fetchBoard(); }
    catch (e) { toast.error('Could not delete'); }
  };

  const removeAttachment = async (postId, attId) => {
    try { await axios.delete(`${API}/comms/board/posts/${postId}/attachments/${attId}`); await fetchBoard(); }
    catch (e) { toast.error('Could not remove attachment'); }
  };

  const download = async (att) => {
    try {
      const r = await axios.get(`${API}/comms/board/attachments/${att.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = att.filename || 'attachment';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not download'); }
  };

  if (!board) return null;
  const posts = board.posts || [];

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 overflow-hidden" data-testid="team-board">
      {/* Header */}
      <button
        onClick={toggle}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-amber-100/50 transition-colors"
        data-testid="team-board-toggle"
      >
        <Pin size={16} className="text-amber-700 shrink-0" />
        <span className="font-semibold text-amber-900">Team Board</span>
        {board.is_new && (
          <span className="text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded-full bg-red-600 text-white animate-pulse" data-testid="team-board-new-badge">
            NEW
          </span>
        )}
        <span className="text-xs text-amber-700/70 ml-auto mr-2 truncate hidden sm:block">
          {board.updated_by ? `Last update by ${resolveName(board.updated_by)} · ${fmtWhen(board.updated_at)}` : 'Shared notes for the team'}
        </span>
        {collapsed ? <ChevronDown size={16} className="text-amber-700" /> : <ChevronUp size={16} className="text-amber-700" />}
      </button>

      {!collapsed && (
        <div className="px-4 pb-4 pt-1 border-t border-amber-200/70" data-testid="team-board-body">
          {/* Posts feed */}
          <div className="space-y-2.5 max-h-80 overflow-y-auto py-2">
            {posts.length === 0 && (
              <p className="text-sm text-amber-800/60 italic py-2">No notes yet — add the first one below.</p>
            )}
            {posts.map((p) => (
              <div key={p.id} className="group text-sm bg-white/70 rounded-lg px-3 py-2 border border-amber-100" data-testid={`team-board-post-${p.id}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-amber-900">{resolveName(p.author_name)}:</span>{' '}
                    {editingId === p.id ? (
                      <div className="mt-1 space-y-2">
                        <div className="relative">
                          <textarea
                            ref={editTextareaRef}
                            value={editText}
                            onChange={onEditTextChange}
                            onKeyDown={onComposerKeyDown}
                            rows={2}
                            className="w-full rounded border px-2 py-1 text-sm resize-none overflow-hidden"
                            placeholder="Edit note…  (type @ to tag someone)"
                            data-testid={`team-board-edit-input-${p.id}`}
                          />
                          {mentionTarget === 'edit' && mentionQuery !== null && (
                            <div className="absolute left-1 right-1 z-30 mt-1 max-h-52 overflow-y-auto rounded-lg border bg-white shadow-lg" data-testid={`team-board-edit-mention-typeahead-${p.id}`}>
                              {mentionMatches.length === 0 ? (
                                <p className="text-xs text-muted-foreground px-3 py-2">No matches with Communications access.</p>
                              ) : (
                                mentionMatches.map((u, idx) => (
                                  <button
                                    type="button"
                                    key={u.id}
                                    onMouseDown={(e) => { e.preventDefault(); selectMention(u); }}
                                    onMouseEnter={() => setMentionActiveIndex(idx)}
                                    className={`w-full flex items-center gap-2 text-left text-sm px-3 py-1.5 ${idx === mentionActiveIndex ? 'bg-blue-50' : 'hover:bg-muted/50'}`}
                                    data-testid={`team-board-edit-mention-option-${u.id}`}
                                  >
                                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-semibold flex items-center justify-center flex-shrink-0">
                                      {(u.name || '?').charAt(0).toUpperCase()}
                                    </span>
                                    <span className="truncate">{u.name}</span>
                                    {editMentions.includes(u.id) && <Check size={13} className="text-green-600 ml-auto" />}
                                  </button>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => saveEdit(p.id)} data-testid={`team-board-save-edit-${p.id}`}><Check size={13} className="mr-1" />Save</Button>
                          <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setMentionQuery(null); }}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <span className="text-foreground whitespace-pre-wrap break-words">{p.content}</span>
                    )}
                    {p.edited && editingId !== p.id && <span className="text-[10px] text-muted-foreground ml-1">(edited)</span>}
                    {/* Mentions */}
                    {p.mentions?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {p.mentions.map((m) => (
                          <span key={m.user_id} className="inline-flex items-center gap-0.5 text-[11px] font-medium text-blue-700 bg-blue-50 rounded-full px-1.5 py-0.5" data-testid={`team-board-mention-${m.user_id}`}>
                            <AtSign size={10} />{m.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Attachments */}
                    {p.attachments?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {p.attachments.map((a) => (
                          <span key={a.id} className="inline-flex items-center gap-1 bg-amber-100 text-amber-900 rounded-full pl-2 pr-1 py-0.5 text-xs" data-testid={`team-board-attachment-${a.id}`}>
                            <button onClick={() => download(a)} className="inline-flex items-center gap-1 hover:underline" title="Download" data-testid={`team-board-download-${a.id}`}>
                              <Download size={11} /> {a.filename}
                            </button>
                            {p.can_edit && (
                              <button onClick={() => removeAttachment(p.id, a.id)} className="hover:bg-amber-200 rounded-full p-0.5" title="Remove attachment" data-testid={`team-board-remove-attachment-${a.id}`}>
                                <X size={11} />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] text-muted-foreground shrink-0 mt-0.5">{fmtWhen(p.created_at)}</span>
                  {p.can_edit && editingId !== p.id && (
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <button onClick={() => beginEdit(p)} className="text-muted-foreground hover:text-foreground p-1" title="Edit" data-testid={`team-board-edit-${p.id}`}><Pencil size={12} /></button>
                      <button onClick={() => remove(p.id)} className="text-muted-foreground hover:text-red-600 p-1" title="Delete" data-testid={`team-board-delete-${p.id}`}><Trash2 size={12} /></button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Composer */}
          <div className="mt-2 rounded-lg border bg-white p-2">
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={text}
                onChange={onTextChange}
                onKeyDown={onComposerKeyDown}
                placeholder="Add a note for the team…  (type @ to tag someone, e.g. @hope)"
                rows={2}
                className="w-full resize-none overflow-hidden text-sm px-2 py-1 outline-none"
                data-testid="team-board-input"
              />
              {/* Inline @mention typeahead */}
              {mentionTarget === 'compose' && mentionQuery !== null && (
                <div
                  className="absolute left-2 right-2 z-30 mt-1 max-h-52 overflow-y-auto rounded-lg border bg-white shadow-lg"
                  data-testid="team-board-mention-typeahead"
                >
                  {mentionMatches.length === 0 ? (
                    <p className="text-xs text-muted-foreground px-3 py-2">No matches with Communications access.</p>
                  ) : (
                    mentionMatches.map((u, idx) => (
                      <button
                        type="button"
                        key={u.id}
                        onMouseDown={(e) => { e.preventDefault(); selectMention(u); }}
                        onMouseEnter={() => setMentionActiveIndex(idx)}
                        className={`w-full flex items-center gap-2 text-left text-sm px-3 py-1.5 ${idx === mentionActiveIndex ? 'bg-blue-50' : 'hover:bg-muted/50'}`}
                        data-testid={`team-board-mention-option-${u.id}`}
                      >
                        <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-semibold flex items-center justify-center flex-shrink-0">
                          {(u.name || '?').charAt(0).toUpperCase()}
                        </span>
                        <span className="truncate">{u.name}</span>
                        {mentions.includes(u.id) && <Check size={13} className="text-green-600 ml-auto" />}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            {files.length > 0 && (
              <div className="flex flex-wrap gap-1.5 px-2 pb-1">
                {files.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-muted rounded-full pl-2 pr-1 py-0.5 text-xs">
                    {f.name}
                    <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="hover:bg-muted-foreground/20 rounded-full p-0.5"><X size={11} /></button>
                  </span>
                ))}
              </div>
            )}
            {/* Selected mention chips */}
            {mentions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 px-2 pb-1">
                {mentions.map((id) => {
                  const u = taggable.find((t) => t.id === id);
                  return (
                    <span key={id} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 rounded-full pl-2 pr-1 py-0.5 text-xs">
                      <AtSign size={11} />{u?.name || 'user'}
                      <button onClick={() => toggleMention(id)} className="hover:bg-blue-200 rounded-full p-0.5"><X size={11} /></button>
                    </span>
                  );
                })}
              </div>
            )}
            <div className="flex items-center justify-between px-1 pt-1 border-t">
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer" data-testid="team-board-attach-label">
                  <Paperclip size={14} /> Attach
                  <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => setFiles([...files, ...Array.from(e.target.files || [])])} data-testid="team-board-file-input" />
                </label>
                <button type="button" onClick={() => setShowTagPicker((v) => !v)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="team-board-tag-btn">
                  <AtSign size={14} /> Tag{mentions.length > 0 ? ` (${mentions.length})` : ''}
                </button>
              </div>
              <Button size="sm" onClick={post} disabled={posting || (!text.trim() && files.length === 0)} data-testid="team-board-post-btn">
                {posting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />} Post
              </Button>
            </div>
            {/* Inline tag picker (rendered in-flow to avoid click/overlap issues) */}
            {showTagPicker && (
              <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border bg-muted/30 p-1" data-testid="team-board-tag-picker">
                <p className="text-[11px] text-muted-foreground px-2 py-1">Tag people with Communications access — they'll be notified.</p>
                {taggable.length === 0 && <p className="text-xs text-muted-foreground px-2 py-1.5">No one with Communications access yet.</p>}
                {taggable.map((u) => (
                  <button
                    type="button"
                    key={u.id}
                    onClick={() => toggleMention(u.id)}
                    className="w-full flex items-center justify-between text-left text-sm px-2 py-1.5 rounded hover:bg-background"
                    data-testid={`team-board-tag-option-${u.id}`}
                  >
                    <span className="flex items-center gap-1"><AtSign size={12} className="text-muted-foreground" />{u.name}</span>
                    {mentions.includes(u.id) && <Check size={14} className="text-green-600" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
